import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { localizePath, stripLangPrefix } from '../utils/i18nPath';

const BASE_URL = 'https://udent.com.ua';
// Recommended SERP-safe description length. Google truncates around 155–160 chars
// (920 px) on desktop, less on mobile. We clamp at 160 to avoid cut-off.
const MAX_DESCRIPTION_LENGTH = 160;
const MAX_TITLE_LENGTH = 60;

const truncate = (str = '', max) => {
    if (!str) return '';
    if (str.length <= max) return str;
    // Cut at last space before max so we don't break mid-word.
    const slice = str.slice(0, max - 1);
    const lastSpace = slice.lastIndexOf(' ');
    return (lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd() + '…';
};

const toAbsoluteUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

/**
 * Reusable SEO <head> component — sets title, description, canonical,
 * Open Graph, Twitter Card, hreflang alternates, and optional JSON-LD
 * structured data per page.
 *
 * Usage:
 *   <SEOHead
 *     title="Ціни — UDent Стоматологія"
 *     description="Прозорі ціни на стоматологічні послуги..."
 *     path="/prices"
 *     schema={{ "@type": "WebPage", ... }}
 *   />
 */
export const SEOHead = ({
    title,
    description,
    path = '/',
    ogType = 'website',
    ogImage,
    ogImageWidth = 1200,
    ogImageHeight = 630,
    ogImageAlt,
    schema,
    noindex = false,
    preloadImage,
}) => {
    const { language } = useLanguage();
    // `path` is the UA (base) path; strip any accidental /en prefix so we
    // always work with canonical UA paths internally.
    const basePath = stripLangPrefix(path);
    const ukUrl = `${BASE_URL}${basePath}`;
    const enUrl = `${BASE_URL}${localizePath(basePath, 'en')}`;
    const canonicalUrl = language === 'en' ? enUrl : ukUrl;
    const locale = language === 'en' ? 'en_US' : 'uk_UA';
    const altLocale = language === 'en' ? 'uk_UA' : 'en_US';

    // Clamp to SERP-safe lengths.
    const safeTitle = truncate(title, MAX_TITLE_LENGTH);
    const safeDescription = truncate(description, MAX_DESCRIPTION_LENGTH);
    const finalOgImage = toAbsoluteUrl(ogImage) || `${BASE_URL}/logo512.png`;
    const finalOgAlt = ogImageAlt || safeTitle;

    return (
        <Helmet>
            {/* Basic */}
            <title>{safeTitle}</title>
            <meta name="description" content={safeDescription} />
            <link rel="canonical" href={canonicalUrl} />
            <html lang={language === 'en' ? 'en' : 'uk'} />

            {/* hreflang alternates — UA at root, EN under /en. x-default
                points to UA (the primary market). */}
            <link rel="alternate" hrefLang="uk" href={ukUrl} />
            <link rel="alternate" hrefLang="en" href={enUrl} />
            <link rel="alternate" hrefLang="x-default" href={ukUrl} />

            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Hero/LCP image preload — improves Largest Contentful Paint
                when the page features a large above-the-fold image. */}
            {preloadImage && (
                <link rel="preload" as="image" href={preloadImage} fetchPriority="high" />
            )}

            {/* Open Graph */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={safeTitle} />
            <meta property="og:description" content={safeDescription} />
            <meta property="og:image" content={finalOgImage} />
            <meta property="og:image:width" content={String(ogImageWidth)} />
            <meta property="og:image:height" content={String(ogImageHeight)} />
            <meta property="og:image:alt" content={finalOgAlt} />
            <meta property="og:locale" content={locale} />
            <meta property="og:locale:alternate" content={altLocale} />
            <meta property="og:site_name" content="UDent" />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={safeTitle} />
            <meta name="twitter:description" content={safeDescription} />
            <meta name="twitter:image" content={finalOgImage} />
            <meta name="twitter:image:alt" content={finalOgAlt} />

            {/* JSON-LD Structured Data — accepts a single schema object or
                an array of schema objects (multiple <script> blocks). */}
            {schema && (Array.isArray(schema) ? schema : [schema]).map((schemaItem, index) => (
                <script key={index} type="application/ld+json">
                    {JSON.stringify({ '@context': 'https://schema.org', ...schemaItem })}
                </script>
            ))}
        </Helmet>
    );
};
